import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TablePagination,
Menu,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Avatar
} from '@mui/material';
import { 
Delete, 
Category, 
PictureAsPdf,TableChart, GetApp,
Image as ImageIcon
} from '@mui/icons-material';

import {
Close as CloseIcon,
Add as AddIcon,
Search as SearchIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import { 
  APIBrancheNamesFilter, 
  APIFoodCategoryList, 
  APIFoodCategoryStore, 
  APIFoodCategoryUpdate, 
  APIFoodCategoryDelete, 
  APIFoodCategoryImage
} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
  formatCreatedAtDate, 
  formatNumberWithComma, 
  formatNumberWithComma2, 
  getCategoriesNameById, 
  getStatusById, 
  getUserDetails, 
  recordsPerPage, 
  statusData 
} from '../../../utils/helpers';
import { 
  exportToCSV, 
  exportToExcel, 
  exportToPDF 
} from '../../../services/restaurant/CategoryService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { apiService } from '../../../apiServices/ApiService';

const FoodCategory = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [referenceData, setReferenceData] = useState({
        branchNames: []
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        status: '',
        description: '',
        image: ''
    });
    const [filters, setFilters] = useState({
        name: '',
        dateFrom: '',
        dateTo: ''
    });
    const [errors, setErrors] = useState({
        name: false,
        description: false,
    });
    
    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);
    
    // Permissions
    const [userRole, setUserRole] = useState('');
    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);
    
    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };
    useEffect(() => {
        fetchData();
        fetchReferenceData();
    }, []);

    const fetchReferenceData = async () => {
        try {
        const [branchesResult] = await Promise.all([
            apiService.branchNames(),
        ]);
        setReferenceData({
            branchNames: branchesResult.success ? branchesResult.data : [],
        });
        } catch (error) {
        showToast("Error", "Failed to load reference data", "error");
        }
    };


    const fetchData = async (branchId = null) => {
        setLoading(true);
        setError(null);

        try {
            let branchIdToUse = branchId;
            if (!hasPermission(PERMISSIONS.BRANCHES)) {
                branchIdToUse = getUserDetails("branchId");
            }

            const params = {};
            if (branchIdToUse) {
                params.branch_id = branchIdToUse;
            }

            const response = await axios.get(APIFoodCategoryList, { params });
            const foodCategories = Array.isArray(response.data.data) ? response.data.data.reverse() : [];

            setPageState(prev => ({
                ...prev,
                pageData: foodCategories,
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            console.log(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleBranchSelect = (branchId) => {
        setSelectedBranch(branchId);
        setBranchSelectionOpen(false);
        fetchData(branchId);
    };

    // Helper function to get initials from name
    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    // Helper function to get avatar color based on name
    const stringToColor = (string) => {
        let hash = 0;
        for (let i = 0; i < string.length; i++) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = '#';
        for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xFF;
            color += ('00' + value.toString(16)).substr(-2);
        }
        return color;
    };

    // Get image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        return `${APIFoodCategoryImage}${imagePath}`;
    };

    // Filter food categories based on search term and filters
    const filteredData = pageState.pageData.filter(foodCategory => {
        const matchesSearch = searchTerm === '' ||
            foodCategory.name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesName = !filters.name || 
            foodCategory.name.toLowerCase().includes(filters.name.toLowerCase());

        let matchesDate = true;
        if (filters.dateFrom) {
            const recordDate = new Date(foodCategory.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const recordDate = new Date(foodCategory.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        setPostStatus("ADD");
        setFormData({
            name: '', 
            description: '',
            status: '',
            image: ''
        });
        setSelectedImage(null);
        setImagePreview('');
        setOpenAdd(true);
    };
    
    const handleCloseAdd = () => { 
        setOpenAdd(false);
        setSelectedImage(null);
        setImagePreview('');
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };
    
    const handleCloseView = () => setOpenView(false);

    const handleOpenEdit = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");
        setFormData({
            name: record.name,
            description: record.description,
            status: record.status,
            image: record.image || ''
        });
        setImagePreview(record.image ? getImageUrl(record.image) : '');
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };
    
    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Image handler
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Remove image
    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview('');
        setFormData(prev => ({
            ...prev,
            image: ''
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const validateForm = () => {
        const errors = [];
        if (!formData.name.trim()) errors.push('Food category name is required');
        if (!formData.description.trim()) errors.push('Description is required');
        if (!String(formData.status || "").trim()) errors.push('Status is required');

        if (errors.length > 0) {
            showToast("Validation Error", errors.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const handleDialogClose = () => {
        setOpenAdd(false);
        setOpenView(false);
        setOpenDelete(false);
        setFilterOptions(false);
        setSelectedImage(null);
        setImagePreview('');
    };

    const handleFormDataReset = () => {
        setFormData({
            name: '',
            status: '',
            description: '',
            image: ''
        });
        setSelectedImage(null);
        setImagePreview('');
    };

    // Export handlers
    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExportExcel = async () => {
        try {
            handleExportClose();
            await exportToExcel(filteredData,referenceData.branchNames);
            showToast("Success", "Excel file downloaded successfully", "success");
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportPDF = async () => {
        try {
            handleExportClose();
            await exportToPDF(filteredData, {
                name: filters.name,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                searchTerm: searchTerm
            },referenceData.branchNames);
            showToast("Success", "PDF file downloaded successfully", "success");
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportCSV = async () => {
        try {
            handleExportClose();
            await exportToCSV(filteredData,referenceData.branchNames);
            showToast("Success", "CSV file downloaded successfully", "success");
        } catch (error) {
            console.error('CSV export error:', error);
            showToast("Error", error.message, "error");
        }
    };
    
    const getSelectedBranchName = () => {
        if (!selectedBranch) return "Select Branch";
        const branch = referenceData.branchNames.find(b => b.id === selectedBranch);
        return branch ? `${branch.name} (${branch.code})` : "Select Branch";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        // Set branch_id based on permissions
        if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
            formDataToSend.append('branch_id', selectedBranch);
        } else {
            formDataToSend.append('branch_id', getUserDetails("branchId"));
        }
        
        formDataToSend.append('name', formData.name);
        formDataToSend.append('status', formData.status);
        formDataToSend.append('description', formData.description);

        // Add image if selected
        if (selectedImage) {
            formDataToSend.append('image', selectedImage);
        }

        // Only append updateId for UPDATE/DELETE operations
        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let apiUrl, method;
            if (postStatus === "ADD") {
                apiUrl = APIFoodCategoryStore;
                method = 'post';
            } else if (postStatus === "UPDATE") {
                apiUrl = APIFoodCategoryUpdate;
                method = 'post';
            } else if (postStatus === "DELETE") {
                apiUrl = APIFoodCategoryDelete;
                method = 'post';
            }

            const response = await axios({
                method,
                url: apiUrl,
                data: formDataToSend,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.status === "success") {
                showToast("Success", response.data.message, "success");
                handleFormDataReset();
                handleDialogClose();
                fetchData();
            } else {
                showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 422) {
                    const errors = data.errors || {};
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(`${field}: ${errors[field].join(', ')}`);
                    }
                    showToast(
                        "Validation Error",
                        errorMessages.join('\n') || "Please correct the form errors",
                        "error"
                    );
                } else if (status === 409) {
                    showToast("Error", data.message || "This record already exists", "error");
                } else if (status >= 500) {
                    showToast("Server Error", data.message || "Internal server error", "error");
                } else {
                    showToast("Error", data.message || "Request failed", "error");
                }
            } else if (error.request) {
                showToast("Network Error", "No response from server. Please check your connection.", "error");
            } else {
                showToast("Error", error.message || "An unexpected error occurred", "error");
            }
            console.error("API Error:", error);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                mb: { xs: 1, sm: 0 }
            }}>
                Food Category Management ( {formatNumberWithComma2(pageState.pageData.length)} Total )
            </Typography>
            
            {/* Display selected branch name */}
            {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
                <Box sx={{ mt: 1, mb: 2 }}>
                    <Chip
                        label={`Selected Branch: ${getSelectedBranchName()}`}
                        color="primary"
                        variant="filled"
                        onDelete={() => {
                            setSelectedBranch('');
                            fetchData();
                        }}
                        deleteIcon={<CloseIcon />}
                    />
                </Box>
            )}

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexShrink: 0,
                        order: { xs: 2, sm: 1 }
                    }}>
                        {hasPermission(PERMISSIONS.ADD) && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Add Food Category
                            </Button>
                        )}

                        {hasPermission(PERMISSIONS.BRANCHES) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<FilterIcon />}
                                onClick={() => setBranchSelectionOpen(true)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Select Branch
                            </Button>
                        )}
                        
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={handlefilterOptions}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            View Options
                        </Button>
                        
                        {hasPermission(PERMISSIONS.EXPORT) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Export Data
                            </Button>
                        )}
                    </Box>

                    <Paper sx={{
                        width: { xs: '100%', sm: '300px' },
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: { xs: 0, sm: 'auto' },
                        order: { xs: 1, sm: 2 },
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search food categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.name || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip 
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip 
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button 
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Image</TableCell>
                                    <TableCell>Date Created</TableCell>
                                    <TableCell>Food Category Name</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Status</TableCell>
                                    {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((foodCategory, index) => {
                                    const initials = getInitials(foodCategory.name);
                                    const avatarColor = stringToColor(foodCategory.name);
                                    const imageUrl = getImageUrl(foodCategory.image);
                                    
                                    return (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {imageUrl ? (
                                                    <Avatar
                                                        src={imageUrl}
                                                        alt={foodCategory.name}
                                                        sx={{ width: 40, height: 40 }}
                                                    />
                                                ) : (
                                                    <Avatar
                                                        sx={{ 
                                                            width: 40, 
                                                            height: 40, 
                                                            bgcolor: avatarColor,
                                                            fontSize: '14px',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {initials}
                                                    </Avatar>
                                                )}
                                            </TableCell>
                                            <TableCell>{formatCreatedAtDate(foodCategory.created_at)}</TableCell>
                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                {foodCategory.name}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ 
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {foodCategory.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {foodCategory.status === 1 ? (
                                                    <Chip
                                                        label={getStatusById(foodCategory.status)}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                ) : (
                                                    <Chip
                                                        label={getStatusById(foodCategory.status)}
                                                        size="small"
                                                        color="error"
                                                    />
                                                )}
                                            </TableCell>
                                            {hasPermission(PERMISSIONS.BRANCHES) && (
                                                <TableCell>
                                                    <Chip
                                                        label={getCategoriesNameById(foodCategory.branch_id, referenceData.branchNames)}
                                                        color="info"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell align="center">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenView(foodCategory)}
                                                    color="primary"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.info.light
                                                        }
                                                    }}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                                {hasPermission(PERMISSIONS.EDIT) && (<>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenEdit(foodCategory)}
                                                        color="secondary"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.success.light
                                                            }
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    </>)}
                                                    {hasPermission(PERMISSIONS.DELETE) && (<>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenDelete(foodCategory)}
                                                        color="error"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.error.light
                                                            }
                                                        }}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </>)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Category sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No food categories found</Typography>
                            <Typography variant="body2">Try adjusting your search query or add a new food category</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Branch Selection Dialog */}
            <Dialog
                open={branchSelectionOpen}
                onClose={() => setBranchSelectionOpen(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel id="branch-select-label">Select Branch</InputLabel>
                            <Select
                                labelId="branch-select-label"
                                value={selectedBranch}
                                label="Select Branch"
                                onChange={(e) => handleBranchSelect(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Select a branch</em>
                                </MenuItem>
                                {referenceData.branchNames.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name} ({branch.code})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => setBranchSelectionOpen(false)}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (selectedBranch) {
                                fetchData(selectedBranch);
                            }
                            setBranchSelectionOpen(false);
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
                <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="info" />
                    Export to CSV (.csv)
                </MenuItem>
            </Menu>

            {/* Options Dialog */}
            <Dialog
                open={filterOptions}
                onClose={handleCloseOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Food Categories
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Food Categories
                        </Button>

                        <Divider>OR</Divider>

                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseOptions}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleCloseOptions}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openAdd}
                onClose={handleCloseAdd}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New Food Category" : "Edit Food Category"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            {/* Image Upload Section */}
                            <Grid item xs={12}>
                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                    {imagePreview ? (
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                            <Avatar
                                                src={imagePreview}
                                                alt="Food Category"
                                                sx={{ 
                                                    width: 120, 
                                                    height: 120,
                                                    mx: 'auto',
                                                    mb: 2
                                                }}
                                            />
                                            <IconButton
                                                onClick={handleRemoveImage}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    backgroundColor: 'error.main',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: 'error.dark'
                                                    }
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Avatar
                                            sx={{ 
                                                width: 120, 
                                                height: 120,
                                                mx: 'auto',
                                                mb: 2,
                                                bgcolor: 'grey.300'
                                            }}
                                        >
                                            <ImageIcon sx={{ fontSize: 60, color: 'grey.500' }} />
                                        </Avatar>
                                    )}
                                    
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<ImageIcon />}
                                        sx={{ mb: 2 }}
                                    >
                                        Upload Image
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={handleImageChange}
                                        />
                                    </Button>
                                    <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                        Optional: Upload a category image (JPG, PNG, GIF, max 2MB)
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Food Category Name"
                                    name="name"
                                    value={formData.name}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.name}
                                    helperText={errors.name ? "Invalid input" : ""}
                                    required
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        autoComplete="off"
                                        onChange={handleInputChange}
                                        label="Status"
                                        required
                                    >
                                        {statusData.map((item, index) => (
                                            <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Description"
                                    name="description"
                                    value={formData.description}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.description}
                                    helperText={errors.description ? "Invalid input" : ""}
                                    required
                                />
                            </Grid>
                        </Box>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleCloseAdd}
                                variant="contained"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                            >
                                {postStatus === "ADD" ? "Add Food Category" : "Update Food Category"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Food Category Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (() => {
                        const initials = getInitials(selectedRecord.name);
                        const avatarColor = stringToColor(selectedRecord.name);
                        const imageUrl = getImageUrl(selectedRecord.image);
                        return (
                            <Box sx={{ p: 2 }}>
                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                    {imageUrl ? (
                                        <Avatar
                                            src={imageUrl}
                                            alt={selectedRecord.name}
                                            sx={{ 
                                                width: 120, 
                                                height: 120,
                                                mx: 'auto',
                                                mb: 2
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            sx={{ 
                                                width: 120, 
                                                height: 120,
                                                mx: 'auto',
                                                mb: 2,
                                                bgcolor: avatarColor,
                                                fontSize: '40px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {initials}
                                        </Avatar>
                                    )}
                                    <Typography variant="h5" gutterBottom>
                                        {selectedRecord.name}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2"><b>Status:</b>
                                            {selectedRecord.status === 1 ? (
                                                <Chip
                                                    label={getStatusById(selectedRecord.status)}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ ml: 1 }}
                                                />
                                            ) : (
                                                <Chip
                                                    label={getStatusById(selectedRecord.status)}
                                                    size="small"
                                                    color="error"
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Typography>
                                    </Grid>
                                    <br />
                                    {hasPermission(PERMISSIONS.BRANCHES) && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Branch:</b>
                                                <Chip
                                                    label={getCategoriesNameById(selectedRecord.branch_id, referenceData.branchNames) || 'N/A'}
                                                    color="info"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ ml: 1 }}
                                                />
                                            </Typography>
                                        </Grid>
                                    )}
                                    <br/>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2"><b>Description:</b></Typography>
                                        <Typography variant="body1" sx={{ 
                                            p: 2, 
                                            bgcolor: 'action.hover', 
                                            borderRadius: 1,
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {selectedRecord.description}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2"><b>Created Date:</b>{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                    </Grid>
                                </Box>
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={handleCloseDelete}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Food Category</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this food category?
                    </Typography>
                    {selectedRecord && (() => {
                        const initials = getInitials(selectedRecord.name);
                        const avatarColor = stringToColor(selectedRecord.name);
                        const imageUrl = getImageUrl(selectedRecord.image);
                        
                        return (
                            <Box sx={{ textAlign: 'center' }}>
                                {imageUrl ? (
                                    <Avatar
                                        src={imageUrl}
                                        alt={selectedRecord.name}
                                        sx={{ 
                                            width: 80, 
                                            height: 80,
                                            mx: 'auto',
                                            mb: 2
                                        }}
                                    />
                                ) : (
                                    <Avatar
                                        sx={{ 
                                            width: 80, 
                                            height: 80,
                                            mx: 'auto',
                                            mb: 2,
                                            bgcolor: avatarColor,
                                            fontSize: '30px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {initials}
                                    </Avatar>
                                )}
                                
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {selectedRecord.name}
                                </Typography>
                                {hasPermission(PERMISSIONS.BRANCHES) && (
                                    <Typography variant="body2">
                                        Branch: {getCategoriesNameById(selectedRecord.branch_id, referenceData.branchNames) || 'N/A'}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseDelete}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default FoodCategory;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

